/**
 * Auth utility functions for accessing authentication state
 * with fallbacks for Safari tracking prevention
 */

// Storage fallback for when localStorage is blocked
let memoryStorage: Record<string, string> = {};

/**
 * Safe localStorage wrapper that falls back to memory storage
 */
const safeStorage = {
  getItem: (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      // Safari tracking prevention fallback
      console.warn('localStorage blocked, using memory storage');
      return memoryStorage[key] || null;
    }
  },
  
  setItem: (key: string, value: string): void => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      // Safari tracking prevention fallback
      console.warn('localStorage blocked, using memory storage');
      memoryStorage[key] = value;
    }
  },
  
  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      // Safari tracking prevention fallback
      delete memoryStorage[key];
    }
  },
  
  clear: (): void => {
    try {
      localStorage.clear();
    } catch (e) {
      // Safari tracking prevention fallback
      memoryStorage = {};
    }
  }
};

/**
 * Get the current auth token from Redux store
 * @returns The auth token or null if not authenticated
 */
export const getAuthToken = (): string | null => {
  const state = (window as any).__REDUX_STORE__?.getState();
  return state?.auth?.token || null;
};

/**
 * Get the current user email from Redux store
 * @returns The user email or null if not authenticated
 */
export const getAuthEmail = (): string | null => {
  const state = (window as any).__REDUX_STORE__?.getState();
  return state?.auth?.email || null;
};

/**
 * Check if user is authenticated
 * @returns True if user has a valid token
 */
export const isAuthenticated = (): boolean => {
  return getAuthToken() !== null;
};

/**
 * Get auth headers for API requests
 * @returns Headers object with Authorization and X-User-Email if authenticated
 */
export const getAuthHeaders = (): Record<string, string> => {
  const token = getAuthToken();
  const email = getAuthEmail();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  if (email) {
    headers['X-User-Email'] = email;
  }
  
  return headers;
};

// Export the safe storage for use in other components
export { safeStorage };